import { Component, input, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoOptions } from '@maskito/core';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';

/**
 * A control that displays an editable text interface.
 */
@Component({
  selector: 'fb-text-field',
  imports: [
    FormsModule,
    MaskitoDirective,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  templateUrl: './text-field.html',
  styleUrl: './text-field.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: TextField, multi: true }],
})
export class TextField implements ControlValueAccessor {
  /**
   * A text that describes the purpose of the text field.
   */
  public readonly label = input.required();

  /**
   * A text that provides users with guidance on what to type into the text field.
   */
  public readonly placeholder = input('');

  /**
   * Configuration for input masking options.
   */
  public readonly mask = input<MaskitoOptions | null>(null);
  public readonly markerTarget = input<ScribeSuggestionMarkerTarget | null>(null);
  /**
   * Index of the treatment row this field belongs to, for the amendment indicator.
   */
  public readonly rowIndex = input<number | null>(null);
  public readonly markerFieldKey = input<string>();

  /**
   * The text to display and edit.
   */
  protected readonly text = signal('');

  /**
   * Called when the control is blurred.
   */
  protected onTouched: () => void;

  /**
   * Called when the control state changes.
   */
  protected onChange: (value: string) => void;

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public writeValue(value: string): void {
    this.text.set(value);
  }

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  /**
   * Handles changes to the text input.
   */
  protected handleTextChange(value: string): void {
    // Update local value.
    this.text.set(value);

    // Propagate change to form control.
    this.onChange(value);
  }
}
